// Short texts and calculations for solution preparation (polymers, solvents,
// separately prepared solutions mixed together). Mirrors the backend's
// SolutionMixing so the form can preview what will be stored.

const num = (v) => (v === null || v === undefined || v === "" ? "?" : String(Number(v)));

/** e.g. "PLGA (LA:GA 75:25 mol%)" or "PCL + GEL 70:30". */
export function polymersText(polymers = []) {
    const named = (polymers || []).filter(p => p?.polymerName);
    if (!named.length) return "";
    const one = (p) => p.composition?.length
        ? `${p.polymerName} (${p.composition.map(f => f.monomer).join(':')} ${p.composition.map(f => f.fraction).join(':')}${p.compositionUnit ? ` ${p.compositionUnit}` : ''})`
        : p.polymerName;
    if (named.length === 1) return one(named[0]);
    return `${named.map(one).join(' + ')} ${named.map(p => num(p.weightRatio)).join(':')}`;
}

/** e.g. "DCM:DMF 7:3". */
export function solventsText(solvents = []) {
    const named = (solvents || []).filter(s => s?.solventName);
    if (!named.length) return "";
    if (named.length === 1) return named[0].solventName;
    return `${named.map(s => s.solventName).join(':')} ${named.map(s => num(s.volumeRatio)).join(':')}`;
}

/** What an additive amount can be given in: a unit and what it is relative to (mg/mL never of the polymer). */
export const ADDITIVE_AMOUNTS = [
    { unit: "wt%", basis: "polymer" },
    { unit: "wt%", basis: "solution" },
    { unit: "wt%", basis: "solvent" },
    { unit: "mg/mL", basis: "solution" },
    { unit: "mg/mL", basis: "solvent" },
];

/** The basis of an amount stored without one: wt% of the polymer, mg/mL of the solution. */
export const amountBasis = (a) => a?.amountBasis || (a?.amountUnit === "mg/mL" ? "solution" : "polymer");

/** e.g. "2 wt% of polymer", or "" without an amount. */
export function additiveAmountText(a) {
    if (a?.amount === null || a?.amount === undefined || a?.amount === "") return "";
    return `${a.amount} ${a.amountUnit || 'wt%'} of ${amountBasis(a)}`;
}

export function additivesText(additives = []) {
    return (additives || []).filter(a => a?.additiveName)
        .map(a => [a.additiveName, additiveAmountText(a)].filter(Boolean).join(' '))
        .join(', ');
}

export const stockLabel = (stock, index) => stock?.label || String.fromCharCode(65 + index);

/**
 * Polymer concentration of a mix of wt% solutions: exact for a w/w mix, an
 * estimate (equal densities) for a v/v mix. Null when a share or concentration is missing.
 */
export function mixedConcentration(stocks = [], mixRatioUnit) {
    if (stocks.length < 2) return null;
    const shares = stocks.map(s => Number(s.share));
    const conc = stocks.map(s => Number(s.concentration));
    if (shares.some(v => !(v > 0)) || conc.some(v => !(v > 0))) return null;
    if (stocks.some(s => (s.concentrationUnit || 'wt%') !== 'wt%')) return null;
    const total = shares.reduce((a, b) => a + b, 0);
    const value = shares.reduce((sum, s, i) => sum + (s / total) * conc[i], 0);
    return { value: Math.round(value * 100) / 100, unit: 'wt%', estimated: mixRatioUnit !== 'w/w' };
}

export const CHANNEL_LABELS = { MAIN: "Solution", CORE: "Core", MIDDLE: "Middle", SHELL: "Shell" };

/** The needle channels that can carry their own solution. */
export function channelsForNeedle(needleType) {
    if (needleType === "Coaxial") return ["CORE", "SHELL"];
    if (needleType === "Triaxial") return ["CORE", "MIDDLE", "SHELL"];
    return [];
}

/** The channels a setup describes separately (empty for one solution). */
export const setupChannels = (setup) =>
    (setup?.solutions || []).map(f => f.channel).filter(c => c && c !== "MAIN");
